import { useState, useEffect } from "react";
import type { NormalisedProduct } from "@app/scripts/types";
import { shopUrl, isMobile } from '../react-hooks/Utilties';


// Function to extract swatch images from product data 
const extractSwatchImages = (products: any) => {
    return products
        .map((product: any) => {
            const skuPartOne = product?.tags.find((tag: string) => tag.includes("x-styleCode:"));
            const skuPartTwo = product?.tags.find((tag: string) => tag.includes("x-colourCode:"));
            const colourMain = product?.tags.find((tag: string) => tag.includes("colour_main"));

            if (!skuPartOne || !skuPartTwo || !colourMain) return null;
            let mainColour = colourMain.split("|")[1];
            let sku = `${skuPartOne?.split(":")[1]}-${skuPartTwo?.split(":")[1]}`;
            if (sku) {
                return {
                    image: `https://${shopUrl}/cdn/shop/files/${sku}.jpg`,
                    backup_image: `https://${shopUrl}/cdn/shop/files/${mainColour}.jpg`,
                    handle: product?.handle,
                    available: product?.published
                };
            }
            return null;
        })
        .filter(Boolean);
};

export const useShopifyProduct = ({ product }: { product?: NormalisedProduct }) => {
    const [swatchImages, setSwatchImages] = useState<string[]>([]);

    // Function to get highlight from product tags
    const getHighlight = (product: any) => {
        let tag = product?.tags?.find((tag: string) => tag.toLowerCase().includes("highlight|"));
        return tag ? tag.split("highlight|")[1] : null;
    };

    // Function to get product badges
    const getProductBadges = (product: any) => {
        let badges = [];

        // Metafields
        const badgesMetafield = product?.metafields?.find((metafield: string) => metafield.key === "badges");
        if (badgesMetafield) badges.push(badgesMetafield.value);

        // Variant Availability
        const productAvailability = product?.variants?.some((variant: any) => variant.available);
        if (!productAvailability) badges.push("Sold Out");

        // Sale
        const firstVariant = product?.variants?.[0];
        if (firstVariant?.compare_at_price) {
            const { compare_at_price: compareAtPrice, price } = firstVariant;
            if (compareAtPrice > price) {
                const savingsPercentage = ((compareAtPrice - price) / compareAtPrice) * 100;
                badges.push(`${savingsPercentage.toFixed(0)}% OFF`);
            }
        }

        // Remove any separators from badges
        badges = badges.flatMap(item => item.includes('/') ? item.split('/') : item);

        // Apply mobile-specific logic
        if (isMobile) {
            badges = badges.includes("Sold Out") ? ["Sold Out"] : badges.slice(0, 1)
        }


        return badges;
    };

    // Function to get sibling data
    const getSiblingsData = (product: any) => {
        let siblingMetafield = product?.metafields?.find((metafield: string) => metafield.key === "siblings");
        return siblingMetafield ? JSON.parse(siblingMetafield?.value) : {}
    };

    const highlight = getHighlight(product);
    const badges = getProductBadges(product);
    const siblingsData = getSiblingsData(product);

    useEffect(() => {
        const fetchProductSwatches = async () => {
            if (siblingsData.length > 0 && !swatchImages.length) {
                const swatchImagesData = extractSwatchImages(siblingsData);
                setSwatchImages(swatchImagesData);
            }
        };

        fetchProductSwatches();
    }, []);

    return {
        highlight,
        badges,
        swatchImages,
        siblingsData,
    };
};
